'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createRef, useEffect, useLayoutEffect, useState } from 'react';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import globalNavHeight from 'nav-meta/global-nav-height';
import { THUNDERDOME, CALYPSO, OLAF } from 'HubStyleTokens/colors';
import { GRID_BREAKPOINT_SMALL, GRID_BREAKPOINT_LARGE } from 'HubStyleTokens/sizes';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import H4 from 'UIComponents/elements/headings/H4';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITruncateString from 'UIComponents/text/UITruncateString';
import * as ScreenSizes from '../../constants/layout/ScreenSizes';
import MainSectionInner from '../layout/MainSectionInner';
import MediaQuery from '../layout/MediaQuery';
import StyledHeaderWrapper from './StyledHeaderWrapper';
import StyledHeaderGrid from './StyledHeaderGrid';
import SearchSelectWrapper from './SearchSelectWrapper';
const StyledSubtitleText = styled.span.withConfig({
  displayName: "StorefrontHeader__StyledSubtitleText",
  componentId: "u5z06y-0"
})(["font-weight:600 !important;-webkit-font-smoothing:antialiased !important;-moz-osx-font-smoothing:grayscale !important;font-size:18px !important;line-height:normal !important;"]);

const StorefrontHeader = ({
  children,
  collapsedTitle,
  compact,
  customBreakpoints,
  customStyle,
  onStickyChange,
  renderedButtonRow,
  subtitle,
  title,
  testId,
  ungatedMobile
}) => {
  // StyledHeaderWrapper gradient colors
  const {
    MAIN_SECTION_CLASSNAME,
    BACKGROUND_COLOR,
    GRADIENT_ANGLE,
    START_GRADIENT_COLOR,
    END_GRADIENT_COLOR,
    SEARCH_PADDING_BOTTOM
  } = customStyle; // StyledHeaderGrid breakpoints

  const {
    GRID_BREAKPOINT_MIN,
    GRID_BREAKPOINT_MAX
  } = customBreakpoints;
  const searchBarContainer = /*#__PURE__*/createRef();
  const [isSticky, setIsSticky] = useState(compact);
  const [stickyOffsetTop, setStickyOffsetTop] = useState(null);
  useEffect(() => {
    if (onStickyChange) {
      onStickyChange(SyntheticEvent(isSticky));
    }
  }, [isSticky, onStickyChange]);
  useLayoutEffect(() => {
    setStickyOffsetTop(findDOMNode(searchBarContainer.current).offsetTop);
  }, []); // eslint-disable-line

  useEffect(() => {
    const scrollHandler = () => {
      const NAV_BAR_HEIGHT_OFFSET = +globalNavHeight.replace(/\D+/g, '');

      if (window.pageYOffset > stickyOffsetTop) {
        setIsSticky(true);
      } else if (window.pageYOffset + NAV_BAR_HEIGHT_OFFSET < stickyOffsetTop) {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  });
  return /*#__PURE__*/_jsxs(StyledHeaderWrapper, {
    className: isSticky ? "sticky" : "",
    "data-test-id": testId,
    backgroundColor: BACKGROUND_COLOR,
    gradientAngle: GRADIENT_ANGLE,
    startGradientColor: START_GRADIENT_COLOR,
    endGradientColor: END_GRADIENT_COLOR,
    children: [!compact && /*#__PURE__*/_jsx(MainSectionInner, {
      className: MAIN_SECTION_CLASSNAME,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        direction: "column",
        children: [/*#__PURE__*/_jsx(UIBox, {
          alignSelf: "end",
          children: /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.MD,
            children: renderedButtonRow
          })
        }), /*#__PURE__*/_jsx("span", {
          "data-test-id": "titleText",
          style: {
            textAlign: 'center',
            color: OLAF
          },
          children: title
        }), /*#__PURE__*/_jsx(StyledSubtitleText, {
          "data-test-id": "subtitleText",
          className: "m-bottom-8",
          style: {
            textAlign: 'center',
            color: OLAF
          },
          children: subtitle
        })]
      })
    }), /*#__PURE__*/_jsx(SearchSelectWrapper, {
      isSticky: isSticky,
      ref: searchBarContainer,
      gradientAngle: GRADIENT_ANGLE,
      startGradientColor: START_GRADIENT_COLOR,
      endGradientColor: END_GRADIENT_COLOR,
      paddingBottom: SEARCH_PADDING_BOTTOM,
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsxs(StyledHeaderGrid, {
          className: isSticky ? "sticky" : "",
          minBreakpoint: GRID_BREAKPOINT_MIN,
          maxBreakpoint: GRID_BREAKPOINT_MAX,
          children: [/*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.SM,
            alternate: isSticky && !ungatedMobile && /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 2,
                md: 4,
                lg: 3
              },
              children: /*#__PURE__*/_jsx(UIMediaObject, {
                align: "baseline",
                className: "p-top-3",
                itemLeft: /*#__PURE__*/_jsx(UIIcon, {
                  name: "sprocket",
                  size: "xs",
                  color: OLAF
                }),
                children: /*#__PURE__*/_jsx(H4, {
                  "aria-level": 4,
                  style: {
                    color: OLAF
                  },
                  className: "m-bottom-0",
                  children: /*#__PURE__*/_jsx(MediaQuery, {
                    minScreenSize: ScreenSizes.MD,
                    children: /*#__PURE__*/_jsx(UITruncateString, {
                      children: collapsedTitle
                    })
                  })
                })
              })
            }),
            children: isSticky && /*#__PURE__*/_jsx(UIGridItem, {
              size: {
                xs: 2,
                md: 4,
                lg: 3
              },
              children: /*#__PURE__*/_jsx(UIMediaObject, {
                align: "baseline",
                className: "p-top-3",
                itemLeft: /*#__PURE__*/_jsx(UIIcon, {
                  name: "sprocket",
                  size: "xs",
                  color: OLAF
                }),
                children: /*#__PURE__*/_jsx(H4, {
                  "aria-level": 4,
                  style: {
                    color: OLAF
                  },
                  className: "m-bottom-0",
                  children: /*#__PURE__*/_jsx(MediaQuery, {
                    minScreenSize: ScreenSizes.MD,
                    children: /*#__PURE__*/_jsx(UITruncateString, {
                      children: collapsedTitle
                    })
                  })
                })
              })
            })
          }), /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.SM,
            alternate: /*#__PURE__*/_jsx(UIGridItem, {
              className: "p-x-0",
              size: isSticky && !ungatedMobile ? {
                xs: 10,
                md: 8,
                lg: 9
              } : 12,
              children: children
            }),
            children: /*#__PURE__*/_jsx(UIGridItem, {
              className: "p-x-0",
              size: isSticky ? {
                xs: 10,
                md: 8,
                lg: 9
              } : 12,
              children: children
            })
          })]
        })
      })
    })]
  });
};

StorefrontHeader.propTypes = {
  children: PropTypes.node,
  collapsedTitle: PropTypes.node.isRequired,
  compact: PropTypes.bool,
  customBreakpoints: PropTypes.shape({
    GRID_BREAKPOINT_MIN: PropTypes.string.isRequired,
    GRID_BREAKPOINT_MAX: PropTypes.string.isRequired
  }),
  customStyle: PropTypes.shape({
    MAIN_SECTION_CLASSNAME: PropTypes.string.isRequired,
    BACKGROUND_COLOR: PropTypes.string.isRequired,
    GRADIENT_ANGLE: PropTypes.string,
    START_GRADIENT_COLOR: PropTypes.string.isRequired,
    END_GRADIENT_COLOR: PropTypes.string.isRequired,
    SEARCH_PADDING_BOTTOM: PropTypes.number
  }),
  onStickyChange: PropTypes.func,
  renderedButtonRow: PropTypes.node,
  subtitle: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  testId: PropTypes.string,
  ungatedMobile: PropTypes.bool
};
StorefrontHeader.defaultProps = {
  compact: false,
  customBreakpoints: {
    GRID_BREAKPOINT_MIN: GRID_BREAKPOINT_SMALL,
    GRID_BREAKPOINT_MAX: GRID_BREAKPOINT_LARGE
  },
  customStyle: {
    MAIN_SECTION_CLASSNAME: 'p-top-7',
    BACKGROUND_COLOR: THUNDERDOME,
    START_GRADIENT_COLOR: THUNDERDOME,
    END_GRADIENT_COLOR: CALYPSO
  }
};
export default StorefrontHeader;